import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@ngneat/dialog';
import { untilDestroyed } from '@ngneat/until-destroy';
import { HotToastService } from '@ngxpert/hot-toast';

import { FormService } from '@core/services';
import { EnumToKeyValueArray, GetUserId, isMobileView, removeNullAndUndefinedProperties } from '@core/utils';
import { UseChat, UseClub } from '@core/usecases';
import { ChatEntity, GroupEntity, UserEntity } from '@core/entities';
import { ChatType } from '@core/enums';
import { DropDownItem } from '@core/types/Dropdown.type';
import { ButtonComponent, DropdownComponent, IconComponent } from '@shared/components/ui';
import { SimpleFileSelectorInputComponent } from '@shared/components/ui/simple-file-selector-input/simple-file-selector-input.component';
import { FormErrorComponent } from '@shared/components/misc/form-error/form-error.component';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { ROLE } from '@auth';

@Component({
  selector: 'app-chat-form',
  imports: [CommonModule, IconComponent, ButtonComponent, FormErrorComponent, FormsModule, NgSelectModule, ReactiveFormsModule, TranslateModule, DropdownComponent, SimpleFileSelectorInputComponent],
  templateUrl: './chat-form.component.html',
  styleUrls: ['./chat-form.component.scss'],
})
export class ChatFormComponent extends FormService implements OnInit {
  teamUsers: UserEntity[];
  groups: GroupEntity[];
  chatTypes: DropDownItem[] = [];
  selectedAttachment: File;

  acceptFileTypes = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpeg,.jpg,.png';

  protected readonly isMobileView = isMobileView();
  protected readonly ChatType = ChatType;
  private readonly _existingConversations: ChatEntity[] = [];

  private readonly _dialogRef: DialogRef;
  private readonly _bottomSheetService: NgxBottomSheetService;

  private readonly _createGroupChatByGroupId: number;
  private readonly _createPersonalChatByUserId: number;

  private readonly _router: Router = inject(Router);
  private readonly _toastService = inject(HotToastService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _translateService = inject(TranslateService);

  private readonly _useClub = new UseClub();
  private readonly _useChat = new UseChat();

  constructor() {
    super();

    if (!this.isMobileView) {
      this._dialogRef = inject(DialogRef);
      this._existingConversations = this._dialogRef.data.existingConversations;
      this._createGroupChatByGroupId = this._dialogRef.data.createGroupChatId;
      this._createPersonalChatByUserId = this._dialogRef.data.createPersonalChatId;
    } else {
      this._bottomSheetService = inject(NgxBottomSheetService);
      this._existingConversations = this._bottomSheetService.currentSheetData.existingConversations;
      this._createGroupChatByGroupId = this._bottomSheetService.currentSheetData.createGroupChatId;
      this._createPersonalChatByUserId = this._bottomSheetService.currentSheetData.createPersonalChatId;
    }
    this._initForm();

    this.chatTypes = EnumToKeyValueArray(ChatType, this._translateService, false);
  }

  ngOnInit() {
    this._fetchGroupsAndTeamUsers();
  }

  close(data?: boolean) {
    if (!this.isMobileView) {
      this._dialogRef.close(data);
    } else {
      this._bottomSheetService.close(data);
    }
  }

  save(btnRef: ButtonComponent) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      for (const control in this.form.controls) {
        if (this.form.controls[control].invalid) {
          console.log(control, this.form.controls[control]);
        }
      }
      return;
    }

    const body = removeNullAndUndefinedProperties(this.form.value);
    body.message = { message: body.message.trim() };

    const formData = this.jsonToFromData(body, ['message']);
    if (this.selectedAttachment) {
      formData.append('file', this.selectedAttachment);
    }

    this.call(this._useChat.saveChatMessage(formData), btnRef)
      .then(() => {
        // this._reloadCurrentRoute();
        this.close(true);
      })
      .catch((error) => {
        this._toastService.error(error.message);
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  onFileSelected(file: Event) {
    const target = file.target as HTMLInputElement;
    if (target.files && target.files.length) {
      this.selectedAttachment = target.files[0];
    }
  }

  private _initForm() {
    this.form = this._formBuilder.group({
      file: [null],
      type: [null, [Validators.required]],
      message: [null, [Validators.required]],
      friendUid: [null, [Validators.required]],
      currentUid: [GetUserId(), [Validators.required]],
      roomId: [null],
    });

    this.form
      .get('friendUid')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            const users = [+value, +GetUserId()].sort((a, b) => a - b);
            let roomId = users[0] + ',' + users[1];

            if (this.form.get('type').value === ChatType.GROUP_CHAT) {
              roomId = this.form.get('friendUid').value;
            }

            this.form.get('roomId').setValue(roomId);
          }
        },
      });
  }

  private _fetchGroupsAndTeamUsers() {
    this._useClub
      .getClubGroupsRoomsAndUsers()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (resp) => {
          this.teamUsers = resp.users;

          this.teamUsers = this.teamUsers.filter((user) => user.role !== ROLE.GUEST);

          this.groups = resp.groups;
          this.groups = this.groups.filter((group) => group.isMember);
          this._filterUsersAndGroups();
        },
      });
  }

  private _filterUsersAndGroups() {
    // This code is commented out because currently it is required to show all users and groups in the chat form

    // if (this._existingConversations && this._existingConversations.length) {
    //   this.teamUsers = this.teamUsers.filter((user) => !this._existingConversations.some((conv) => conv.type === ChatType.Individual && +conv.id === +user.id));
    //   this.groups = this.groups.filter((group) => !this._existingConversations.some((conv) => conv.type === ChatType.Group && +conv.id === +group.id));
    // }

    if (this._createGroupChatByGroupId && +this._createGroupChatByGroupId > 0) {
      this.form.get('type').setValue(ChatType.GROUP_CHAT);
      this.form.get('friendUid').setValue(+this._createGroupChatByGroupId);
    }

    if (this._createPersonalChatByUserId && +this._createPersonalChatByUserId > 0) {
      this.form.get('type').setValue(ChatType.PERSONAL_CHAT);
      this.form.get('friendUid').setValue(+this._createPersonalChatByUserId);

      // Hard check for birthday message
      // Done this way because the birthday message is not coming from the API and is not essential to the chat form
      if (this._router.url.includes('isBirthday=true')) {
        this.form.get('message').setValue(this._translateService.instant('Happy Birthday') + ' 🎂');
      } else if (this._router.url.includes('isJubilee=true')) {
        this.form.get('message').setValue(this._translateService.instant('Happy Anniversary') + ' 🎉');
      }
    }
  }
}
